import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Phone, MessageSquare, Check, Clock, AlertCircle, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  hoursLeft24,
  formatCountdown,
  getInitials,
  typeAppartLabel,
  localisationLabel,
  hoursSince,
} from "@/lib/crmHelpers";

/**
 * Circuit d'appel 24h — leads nouveaux non encore contactés dans les 24h
 * Montre un compte à rebours par lead.
 */
export default function Circuit24hSection({ leads, onOpenLead, onAction, onProcedureToggle }) {
  const [, setTick] = useState(0);
  // Tick every minute to refresh countdowns
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const circuitLeads = useMemo(() => {
    return leads
      .filter((l) => l.statut === "nouveau" && !l.procedure?.contacted_24h)
      .map((l) => ({ ...l, _left: hoursLeft24(l.created_at) }))
      .sort((a, b) => (a._left ?? 0) - (b._left ?? 0));
  }, [leads]);

  if (circuitLeads.length === 0) return null;

  const retard = circuitLeads.filter((l) => (l._left ?? 0) <= 0).length;
  const urgent = circuitLeads.filter((l) => (l._left ?? 0) > 0 && (l._left ?? 0) < 2).length;

  return (
    <section data-testid="circuit-24h-section">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <div className="text-label text-muted-foreground">Appels prioritaires</div>
          <h2 className="font-heading mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <PhoneCall className="h-6 w-6 text-[hsl(var(--accent-navy))]" />
            Circuit d'appel 24h
          </h2>
        </div>
        <div className="flex items-center gap-3 text-xs">
          {retard > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-1 font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300">
              <AlertCircle className="h-3 w-3" />
              {retard} en retard
            </span>
          )}
          {urgent > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
              <Clock className="h-3 w-3" />
              {urgent} urgent
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {circuitLeads.map((lead) => {
          const left = lead._left ?? 0;
          const overdue = left <= 0;
          const urgent = !overdue && left < 2;
          const handleCall = async (e) => {
            e.stopPropagation();
            await onAction(lead.id, "appel", `Appel vers ${lead.telephone}`);
            await onProcedureToggle(lead.id, "contacted_24h", true);
            toast.success(`Appel ${lead.nom} enregistré · Circuit validé`);
            window.location.href = `tel:${lead.telephone.replace(/\s+/g, "")}`;
          };
          const handleDone = async (e) => {
            e.stopPropagation();
            await onProcedureToggle(lead.id, "contacted_24h", true);
            toast.success(`${lead.nom} marqué contacté`);
          };
          return (
            <div
              key={lead.id}
              data-testid={`circuit-24h-${lead.id}`}
              onClick={() => onOpenLead(lead)}
              className={`group relative flex cursor-pointer items-center gap-3 rounded-xl border bg-card p-3 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm ${
                overdue
                  ? "border-red-300 bg-red-50/30 dark:border-red-900/50 dark:bg-red-950/20"
                  : urgent
                  ? "border-amber-300 dark:border-amber-900/50"
                  : "border-border/60"
              }`}
            >
              <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-muted font-heading text-xs font-semibold">
                {getInitials(lead.nom)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-heading truncate text-sm font-semibold">{lead.nom}</div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span>{typeAppartLabel(lead.type_appart).toUpperCase()}</span>
                  <span>·</span>
                  <span>{localisationLabel(lead.localisation)}</span>
                </div>
                <div
                  className={`mt-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${
                    overdue
                      ? "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300"
                      : urgent
                      ? "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  <Clock className="h-3 w-3" />
                  {formatCountdown(left)}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCall}
                  data-testid={`circuit-call-${lead.id}`}
                  className="h-7 gap-1 px-2 text-xs"
                >
                  <Phone className="h-3 w-3" />
                  Appeler
                </Button>
                <Button
                  size="sm"
                  onClick={handleDone}
                  data-testid={`circuit-done-${lead.id}`}
                  className="h-7 gap-1 px-2 text-xs"
                >
                  <Check className="h-3 w-3" />
                  Traité
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
